<script setup lang="ts">
import { useBillingRequests } from '@/composables/billing/useBillingRequests';
import { useBillingProducts } from '@/composables/billing/useBillingProducts';
import { useBillingPaymentMethod, useBillingCheckoutOptions } from '@/composables/billing/billing.queries';
import { useBillingMutations } from '@/composables/billing/useBillingMutations';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import BillingCheckoutOverviewStep from './BillingCheckoutOverviewStep.vue';
import BillingCheckoutPlanSelectionStep from './BillingCheckoutPlanSelectionStep.vue';
import BillingCheckoutPaymentMethodStep from './BillingCheckoutPaymentMethodStep.vue';
import BillingCheckoutInvoiceAddressStep from './BillingCheckoutInvoiceAddressStep.vue';
import BillingCheckoutInvoiceSuccessStep from './BillingCheckoutInvoiceSuccessStep.vue';
import BillingDialogHeader from './BillingDialogHeader.vue';
import BillingOfferDialog from '@/core/dialogs/billing-offer/BillingOfferDialog.vue';
import { Components } from '@/api/generated/api';
import CheckoutSuccessMainContent from '../billing-success/CheckoutSuccessMainContent.vue';
import { useBillingState } from '@/composables/billing/useBillingState';
import LoadingSpinner from '@/core/elements/LoadingSpinner.vue';
import {
  getBillingIntervalLabel,
  getDiscountPercentForCheckoutSource,
  getBillingInvoiceAddressDraft,
  getMaxTrialUpsellDiscountPercent,
  getPlanSwitchEffectDescription,
  isProductAvailableForPurchase,
  type BillingInterval,
  type ProductWithMeta,
} from '@/utils/billing';
import IconButton from '@/core/elements/IconButton.vue';
import { EIcon } from 'common/enum/misc.enum';
import { BillingCollectionMethod } from '@/api/generated/api.enum';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';
import { clearPendingCheckoutContext, savePendingCheckoutContext } from '@/services/analytics/checkoutStorage';
import {
  buildCheckoutTrackingPayload,
  CheckoutOfferSource,
  createPlanSwitchTransactionId,
  getCheckoutOfferShowEvents,
  trackCheckoutSuccessAndPurchaseEvents,
  type CheckoutOfferSource as CheckoutOfferSourceType,
} from '@/services/analytics/checkout';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useToast } from '@/composables/useToast';
import { useBillingInvoiceCreateConfirm } from '@/composables/billing/useBillingInvoiceCreateConfirm';
import { useI18n } from 'vue-i18n';
import type { PaymentMethod } from './billingCheckoutFlow';
import { useDialog } from '@/core/dialogsystem/useDialog';

enum EBillingCheckoutStep {
  OVERVIEW = 'OVERVIEW',
  PLAN_SELECTION = 'PLAN_SELECTION',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  INVOICE_ADDRESS = 'INVOICE_ADDRESS',
  INVOICE_SUCCESS = 'INVOICE_SUCCESS',
  SUCCESS = 'SUCCESS',
}

const props = defineProps<{
  viewBillingDocuments?: () => void;
}>();

const isOpen = ref(false);
const { canManageBilling, canCreateBillingOffer, billingStatus, isBaseAfterTrial, isTrialEnded } = useBillingState();
const { hasBillablePaymentMethod } = useBillingPaymentMethod(isOpen);
const { checkoutTenant, updateTenantInvoiceAddress, checkoutTenantViaInvoice, switchTenantPlan } =
  useBillingMutations(billingStatus);
const { previewTenantCheckout, previewTenantPlanSwitch, getTenantInvoiceAddress } =
  useBillingRequests(canManageBilling);
const { allBillingProductsWithMeta } = useBillingProducts(isOpen);
const { checkoutOptions } = useBillingCheckoutOptions(isOpen);

const { trackEvent } = useAnalytics();
const { confirm } = useConfirmDialog();
const { customSuccessToast } = useToast();
const { confirmBillingInvoiceCreate } = useBillingInvoiceCreateConfirm();
const { t } = useI18n();
const billingOfferDialog = useDialog({
  component: BillingOfferDialog,
  props: {
    viewBillingDocuments: props.viewBillingDocuments,
  },
});

const dialogRef = useTemplateRef('dialogRef');
const currentStep = ref<EBillingCheckoutStep>(EBillingCheckoutStep.OVERVIEW);
const isSubmitting = ref(false);
const selectedPlan = ref<Components.Schemas.ProductTypeDto | undefined>();
const selectedQuantity = ref<number | undefined>();
const selectedTeamQuantity = ref<number | undefined>();
const selectedStudentQuantity = ref<number | undefined>();
const selectedBillingInterval = ref<BillingInterval>('year');
const selectedInvoicePaymentRequested = ref(false);
const selectedPaymentMethod = ref<PaymentMethod>('online');
const checkoutPreview = ref<Components.Schemas.BillingCheckoutPreviewDtoData | null>(null);
const invoiceAddress = ref<Components.Schemas.BillingInvoiceAddressDto | null>(null);
const invoiceAddressDraft = ref<Components.Schemas.UpsertTenantInvoiceAddressDto | null>(null);
const invoice = ref<Components.Schemas.BillingInvoiceDto | null>(null);
const checkoutSource = ref<Components.Schemas.CheckoutSource>('default');
const checkoutOfferSource = ref<CheckoutOfferSourceType | null>(null);
const initialInterval = ref<BillingInterval>('year');
const shouldTrackOfferShow = ref(true);
const overviewFeature = ref<Components.Schemas.ProductFeature | null>(null);
const closeReason = ref<'dismiss' | 'redirect_to_checkout' | 'completed'>('dismiss');

const hasAnyHistory = computed<boolean>(() => !!billingStatus.value?.hasAnySubscriptionHistory);
const isActiveOrTrial = computed<boolean>(() => {
  const status = billingStatus.value;
  if (!status) return false;
  return status.hasActivePlan || status.isTrialActive;
});

const isActiveInvoiceSubscription = computed<boolean>(() => {
  return isActiveOrTrial.value && billingStatus.value?.collectionMethod === BillingCollectionMethod.sendInvoice;
});

const paymentMethodOk = computed<boolean>(
  () => hasBillablePaymentMethod.value === true || isActiveInvoiceSubscription.value,
);

const defaultCheckoutOfferSource = computed<CheckoutOfferSourceType | null>(() => {
  if (billingStatus.value?.isTrialActive) return CheckoutOfferSource.TrialAdminPlans;
  if (isBaseAfterTrial.value || isTrialEnded.value) return CheckoutOfferSource.BasisAdminPlans;
  return null;
});

const needsCheckout = computed<boolean>(() => {
  return !hasAnyHistory.value || !isActiveOrTrial.value || !paymentMethodOk.value;
});

const trialOverviewDiscountPercent = computed<number>(() => {
  if (checkoutSource.value !== 'trialUpsell' || billingStatus.value?.isTrialActive !== true) return 0;

  return getMaxTrialUpsellDiscountPercent(
    allBillingProductsWithMeta.value.filter(isProductAvailableForPurchase),
    'year',
  );
});

const getPlanWithMeta = (plan?: Components.Schemas.ProductTypeDto): ProductWithMeta | undefined =>
  allBillingProductsWithMeta.value.find((product) => product.id === plan?.id);

const getPlanWithMetaById = (productTypeId: number | null): ProductWithMeta | undefined =>
  allBillingProductsWithMeta.value.find((product) => product.id === productTypeId);

const getQuantityUnitLabel = (plan: ProductWithMeta): string | null => {
  if (!plan.supportsSeats) return null;
  if (plan.product === 'school') return t('billing.products.school.checkoutDescriptionUnit');
  if (plan.product === 'team') return t('billing.products.team.checkoutDescriptionUnit');
  return null;
};

const formatPlanSwitchSummary = (params: {
  productTypeId: number | null;
  billingInterval: BillingInterval | null;
  quantity: number | null;
}): string => {
  const plan = getPlanWithMetaById(params.productTypeId);
  const planLabel = plan ? t(plan.meta.titleKey) : t('billing.planSwitchConfirm.unknownPlan');
  const details = [getBillingIntervalLabel(params.billingInterval, t)];
  const quantityUnit = plan ? getQuantityUnitLabel(plan) : null;
  if (quantityUnit && params.quantity != null) {
    details.push(t('billing.planSwitchConfirm.quantity', { quantity: params.quantity, unit: quantityUnit }));
  }

  return `${planLabel} (${details.join(', ')})`;
};

const confirmPlanSwitch = async (preview: Components.Schemas.BillingPlanSwitchPreviewDtoData): Promise<boolean> => {
  return confirm({
    title: t('billing.planSwitchConfirm.title'),
    description: t('billing.planSwitchConfirm.description', {
      current: formatPlanSwitchSummary({
        productTypeId: preview.currentProductTypeId,
        billingInterval: preview.currentBillingInterval,
        quantity: preview.currentQuantity,
      }),
      target: formatPlanSwitchSummary({
        productTypeId: preview.targetProductTypeId,
        billingInterval: preview.targetBillingInterval,
        quantity: preview.targetQuantity,
      }),
      effect: getPlanSwitchEffectDescription(preview, t),
    }),
    confirmButtonLabel: t('billing.planSwitchConfirm.confirmButton'),
    declineButtonLabel: t('general.cancel'),
    theme: 'primary',
    size: 'lg',
    confirmButtonIcon: EIcon.CHECK,
    buttonAlignment: 'between',
  });
};

const confirmCancelScheduledPlanChange = async (
  preview: Components.Schemas.BillingPlanSwitchPreviewDtoData,
): Promise<boolean> => {
  return confirm({
    title: t('billing.planSwitchConfirm.cancelScheduledChangeTitle'),
    description: t('billing.planSwitchConfirm.cancelScheduledChangeDescription', {
      current: formatPlanSwitchSummary({
        productTypeId: preview.currentProductTypeId,
        billingInterval: preview.currentBillingInterval,
        quantity: preview.currentQuantity,
      }),
    }),
    confirmButtonLabel: t('billing.planSwitchConfirm.cancelScheduledChangeConfirmButton'),
    declineButtonLabel: t('general.cancel'),
    theme: 'primary',
    size: 'lg',
    confirmButtonIcon: EIcon.CHECK,
    buttonAlignment: 'between',
  });
};

const shouldShowInvoiceSuccessForPlanSwitch = (
  preview: Components.Schemas.BillingPlanSwitchPreviewDtoData,
): boolean => {
  return isActiveInvoiceSubscription.value && preview.createsImmediateInvoice && preview.amountDueNowCents !== 0;
};

const cancelScheduledPlanChange = async (
  preview: Components.Schemas.BillingPlanSwitchPreviewDtoData,
  params: { plan: Components.Schemas.ProductTypeDto; quantity?: number; billingInterval: BillingInterval },
): Promise<void> => {
  isSubmitting.value = false;
  const confirmed = await confirmCancelScheduledPlanChange(preview);
  if (!confirmed) return;

  isSubmitting.value = true;
  clearPendingCheckoutContext();
  // Re-selecting the current plan while a change is scheduled releases the Stripe schedule.
  await switchTenantPlan(params.plan.id, params.quantity, params.billingInterval);
  customSuccessToast(t('billing.planSwitchConfirm.cancelScheduledChangeSuccess'));
  hide('completed');
};

const buildCheckoutPayload = (
  plan?: Components.Schemas.ProductTypeDto,
  quantity?: number,
  billingInterval: BillingInterval = initialInterval.value,
): ReturnType<typeof buildCheckoutTrackingPayload> & {
  has_subscription_history: boolean;
  needs_redirect_checkout: boolean;
} => {
  const planWithMeta = getPlanWithMeta(plan);
  const winbackDiscountPercent = planWithMeta
    ? getDiscountPercentForCheckoutSource(checkoutSource.value, planWithMeta, {
        interval: billingInterval,
        winbackOffer: checkoutOptions.value?.winbackOffer,
      })
    : undefined;

  return {
    ...buildCheckoutTrackingPayload({
      billingInterval,
      discountPercent: checkoutSource.value === 'trialWinback' ? winbackDiscountPercent : undefined,
      offerSource: checkoutOfferSource.value,
      plan: planWithMeta,
      quantity,
      source: checkoutSource.value,
    }),
    has_subscription_history: hasAnyHistory.value,
    needs_redirect_checkout: needsCheckout.value,
  };
};

const showNextStep = (): void => {
  switch (currentStep.value) {
    case EBillingCheckoutStep.OVERVIEW:
      currentStep.value = EBillingCheckoutStep.PLAN_SELECTION;
      break;
    case EBillingCheckoutStep.PLAN_SELECTION:
      currentStep.value = EBillingCheckoutStep.SUCCESS;
      break;
    case EBillingCheckoutStep.PAYMENT_METHOD:
      currentStep.value = EBillingCheckoutStep.INVOICE_ADDRESS;
      break;
  }
};

const handleOverviewStepDone = (): void => {
  if (!canManageBilling.value) return;
  showNextStep();
};

type PlanSelectionParams = {
  plan: Components.Schemas.ProductTypeDto;
  quantity?: number;
  billingInterval: BillingInterval;
  invoicePaymentRequested: boolean;
};

const handlePlanSelectionStepDone = async (params: PlanSelectionParams): Promise<void> => {
  if (!canManageBilling.value || isSubmitting.value) return;
  selectedPlan.value = params.plan;
  selectedQuantity.value = params.quantity;
  selectedBillingInterval.value = params.billingInterval;
  selectedInvoicePaymentRequested.value = params.invoicePaymentRequested;
  try {
    isSubmitting.value = true;
    const trackingPayload = buildCheckoutPayload(params.plan, params.quantity, params.billingInterval);
    trackEvent(AnalyticsEvents.checkout.submit, trackingPayload);

    if (needsCheckout.value) {
      await startNewPlanCheckout(params);
      return;
    }
    await performPlanSwitch(params, trackingPayload);
  } finally {
    isSubmitting.value = false;
  }
};

/** New purchase: straight to the hosted checkout, or the invoice-payment step when requested AND eligible. */
const startNewPlanCheckout = async (params: PlanSelectionParams): Promise<void> => {
  if (!params.invoicePaymentRequested) {
    await redirectToOnlineCheckout();
    return;
  }

  checkoutPreview.value = await previewTenantCheckout(params.plan.id, {
    quantity: params.quantity,
    source: checkoutSource.value,
    billingInterval: params.billingInterval,
  });

  if (checkoutPreview.value?.invoiceEligible) {
    selectedPaymentMethod.value = 'online';
    currentStep.value = EBillingCheckoutStep.PAYMENT_METHOD;
    return;
  }

  await redirectToOnlineCheckout();
};

/** Existing subscription: preview the switch, let the user confirm, then apply and track it. */
const performPlanSwitch = async (
  params: PlanSelectionParams,
  trackingPayload: ReturnType<typeof buildCheckoutPayload>,
): Promise<void> => {
  const switchPreview = await previewTenantPlanSwitch(params.plan.id, {
    quantity: params.quantity,
    billingInterval: params.billingInterval,
  });
  if (!switchPreview) return;

  if (switchPreview.effect === 'noChange') {
    if (!switchPreview.releasesScheduledChange) return;

    await cancelScheduledPlanChange(switchPreview, params);
    return;
  }

  // Release the guard while the confirm dialog is open; reacquire for the mutating call.
  isSubmitting.value = false;
  const confirmed = await confirmPlanSwitch(switchPreview);
  if (!confirmed) return;

  const showInvoiceSuccess = shouldShowInvoiceSuccessForPlanSwitch(switchPreview);
  isSubmitting.value = true;
  clearPendingCheckoutContext();
  await switchTenantPlan(params.plan.id, params.quantity, params.billingInterval);
  invoice.value = billingStatus.value?.pendingInvoice ?? null;
  trackCheckoutSuccessAndPurchaseEvents({
    extraPayload: { flow: 'plan_switch' },
    payload: trackingPayload,
    trackEvent,
    transactionId: createPlanSwitchTransactionId(trackingPayload),
  });
  if (showInvoiceSuccess) {
    currentStep.value = EBillingCheckoutStep.INVOICE_SUCCESS;
    return;
  }

  showNextStep();
};

const redirectToOnlineCheckout = async (): Promise<void> => {
  if (!selectedPlan.value) return;
  const trackingPayload = buildCheckoutPayload(
    selectedPlan.value,
    selectedQuantity.value,
    selectedBillingInterval.value,
  );
  savePendingCheckoutContext(trackingPayload);

  const url = await checkoutTenant(selectedPlan.value.id, {
    quantity: selectedQuantity.value,
    source: checkoutSource.value,
    billingInterval: selectedBillingInterval.value,
    checkoutOfferSource: checkoutOfferSource.value,
  });
  if (url) {
    hide('redirect_to_checkout');
    window.location.assign(url);
    return;
  }

  clearPendingCheckoutContext();
};

const handlePaymentMethodOnline = async (): Promise<void> => {
  if (!canManageBilling.value || isSubmitting.value) return;
  try {
    isSubmitting.value = true;
    await redirectToOnlineCheckout();
  } finally {
    isSubmitting.value = false;
  }
};

const handlePaymentMethodInvoice = async (): Promise<void> => {
  if (!canManageBilling.value || isSubmitting.value) return;
  try {
    isSubmitting.value = true;
    invoiceAddress.value = await getTenantInvoiceAddress();
    invoiceAddressDraft.value ??= getBillingInvoiceAddressDraft(invoiceAddress.value);
    currentStep.value = EBillingCheckoutStep.INVOICE_ADDRESS;
  } finally {
    isSubmitting.value = false;
  }
};

const handlePaymentMethodStepDone = async (method: PaymentMethod): Promise<void> => {
  selectedPaymentMethod.value = method;
  if (method === 'online') {
    await handlePaymentMethodOnline();
    return;
  }
  await handlePaymentMethodInvoice();
};

const handlePlanSelectionQuantityDraftChange = (params: { teamQuantity: number; studentQuantity: number }): void => {
  selectedTeamQuantity.value = params.teamQuantity;
  selectedStudentQuantity.value = params.studentQuantity;
};

const handlePlanSelectionPlanDraftChange = (plan: Components.Schemas.ProductTypeDto): void => {
  selectedPlan.value = plan;
};

const handlePlanSelectionIntervalDraftChange = (billingInterval: BillingInterval): void => {
  selectedBillingInterval.value = billingInterval;
};

const handleInvoiceAddressStepDone = async (
  address: Components.Schemas.UpsertTenantInvoiceAddressDto,
): Promise<void> => {
  if (!canManageBilling.value || isSubmitting.value || !selectedPlan.value) return;
  const confirmed = await confirmBillingInvoiceCreate();
  if (!confirmed) return;

  try {
    isSubmitting.value = true;
    invoiceAddressDraft.value = address;
    await updateTenantInvoiceAddress(address);
    invoice.value = await checkoutTenantViaInvoice(selectedPlan.value.id, {
      quantity: selectedQuantity.value,
      source: checkoutSource.value,
      billingInterval: selectedBillingInterval.value,
    });

    if (!invoice.value) return;

    const trackingPayload = buildCheckoutPayload(
      selectedPlan.value,
      selectedQuantity.value,
      selectedBillingInterval.value,
    );
    clearPendingCheckoutContext();
    // No purchase yet: the server reports it to the ad platforms once the invoice is paid.
    trackEvent(AnalyticsEvents.checkout.success, {
      ...trackingPayload,
      flow: 'invoice',
      invoice_id: invoice.value.billingInvoiceId,
    });
    currentStep.value = EBillingCheckoutStep.INVOICE_SUCCESS;
  } finally {
    isSubmitting.value = false;
  }
};

const handleBackFromPaymentMethod = (): void => {
  currentStep.value = EBillingCheckoutStep.PLAN_SELECTION;
};

const handleBackFromInvoiceAddress = (): void => {
  currentStep.value = EBillingCheckoutStep.PAYMENT_METHOD;
};

const show = (params: {
  skipOverview?: boolean;
  initialPlan?: Components.Schemas.ProductTypeDto;
  offerSource?: CheckoutOfferSourceType | null;
  source?: Components.Schemas.CheckoutSource;
  initialInterval?: BillingInterval;
  trackOfferShow?: boolean;
  feature?: Components.Schemas.ProductFeature | null;
}): void => {
  isOpen.value = true;
  checkoutSource.value = params.source ?? 'default';
  checkoutOfferSource.value = params.offerSource ?? defaultCheckoutOfferSource.value;
  initialInterval.value = params.initialInterval ?? 'year';
  shouldTrackOfferShow.value = params.trackOfferShow ?? true;
  overviewFeature.value = params.feature ?? null;
  currentStep.value = EBillingCheckoutStep.OVERVIEW;
  selectedPlan.value = params.initialPlan;
  selectedQuantity.value = undefined;
  selectedTeamQuantity.value = undefined;
  selectedStudentQuantity.value = undefined;
  selectedBillingInterval.value = initialInterval.value;
  selectedInvoicePaymentRequested.value = false;
  selectedPaymentMethod.value = 'online';
  checkoutPreview.value = null;
  invoiceAddress.value = null;
  invoiceAddressDraft.value = null;
  invoice.value = null;
  if (params.initialPlan) {
    currentStep.value = EBillingCheckoutStep.PLAN_SELECTION;
  }

  if (params.skipOverview) currentStep.value = EBillingCheckoutStep.PLAN_SELECTION;

  const trackingPayload = buildCheckoutPayload(selectedPlan.value);
  const offerShowEvents = getCheckoutOfferShowEvents(checkoutOfferSource.value);
  if (shouldTrackOfferShow.value) {
    for (const offerShowEvent of offerShowEvents) {
      trackEvent(offerShowEvent, trackingPayload);
    }
  }
  trackEvent(AnalyticsEvents.checkout.dialogOpen, trackingPayload);

  dialogRef.value?.show();
};

const hide = (reason: 'dismiss' | 'redirect_to_checkout' | 'completed' = 'dismiss'): void => {
  if (reason === 'dismiss' && isSubmitting.value) return;

  closeReason.value = reason;
  dialogRef.value?.hide();
};

const handleDialogHidden = (): void => {
  isOpen.value = false;
  trackEvent(AnalyticsEvents.checkout.dialogClose, {
    ...buildCheckoutPayload(selectedPlan.value),
    reason: closeReason.value,
  });
  closeReason.value = 'dismiss';
};

const handleViewBillingDocuments = (): void => {
  hide('completed');
  props.viewBillingDocuments?.();
};

const handleCreateOfferFromPlanSelection = async (): Promise<void> => {
  if (!canCreateBillingOffer.value || isSubmitting.value) return;
  hide();
  await nextTick();
  billingOfferDialog.show({
    initialPlan: selectedPlan.value,
    initialInterval: selectedBillingInterval.value,
    initialTeamQuantity: selectedTeamQuantity.value,
    initialStudentQuantity: selectedStudentQuantity.value,
    checkoutSource: checkoutSource.value,
  });
};

watch(currentStep, async () => {
  await nextTick();
  dialogRef.value?.scrollToTop();
});

const selectedPlanWithMeta = computed<ProductWithMeta | undefined>(() =>
  allBillingProductsWithMeta.value.find((pM) => pM.id === selectedPlan.value?.id),
);

const isCompactHeightStep = computed<boolean>(
  () =>
    currentStep.value === EBillingCheckoutStep.PAYMENT_METHOD ||
    currentStep.value === EBillingCheckoutStep.INVOICE_ADDRESS ||
    currentStep.value === EBillingCheckoutStep.INVOICE_SUCCESS,
);
const isPlanSelectionStep = computed<boolean>(() => currentStep.value === EBillingCheckoutStep.PLAN_SELECTION);
const isOverviewStep = computed<boolean>(() => currentStep.value === EBillingCheckoutStep.OVERVIEW);

defineExpose({
  show,
});
</script>

<template>
  <DialogBase
    ref="dialogRef"
    :show-header="false"
    enter-animation="slide-up"
    :content-width-style="
      isOverviewStep
        ? 'w-[640px] max-w-[calc(100vw-32px)] max-md:w-screen max-md:max-w-full'
        : 'w-[960px] max-w-[calc(100vw-32px)] max-md:w-screen max-md:max-w-full'
    "
    no-padding
    :dialog-height-style="
      isOverviewStep
        ? undefined
        : isCompactHeightStep
          ? 'h-[640px] !max-h-[92vh] max-md:h-dvh max-md:!max-h-dvh'
          : isPlanSelectionStep
            ? selectedBillingInterval === 'year'
              ? 'h-[725px] !max-h-[92vh] max-md:h-dvh max-md:!max-h-dvh'
              : 'h-[675px] !max-h-[92vh] max-md:h-dvh max-md:!max-h-dvh'
            : 'h-[750px] !max-h-[92vh] max-md:h-dvh max-md:!max-h-dvh'
    "
    :disable-internal-hide-logic="isSubmitting"
    @hide="handleDialogHidden"
  >
    <template #above-header>
      <div v-if="isOverviewStep" class="relative h-[80px] min-h-[80px] shrink-0">
        <IconButton
          class="absolute right-lg top-lg max-md:bg-surface-secondary"
          type="button"
          size="button"
          :icon-name="EIcon.X"
          :aria-label="$t('general.close')"
          theme="text"
          @click="hide()"
        />
      </div>
      <BillingDialogHeader
        v-else-if="currentStep === EBillingCheckoutStep.PLAN_SELECTION"
        :title="$t('billing.buttons.chooseSubscription')"
        @close="hide()"
      />
    </template>

    <div class="relative h-full">
      <BillingCheckoutOverviewStep
        v-if="currentStep === EBillingCheckoutStep.OVERVIEW"
        :can-manage-billing="canManageBilling"
        :disabled="isSubmitting"
        :feature="overviewFeature"
        :trial-discount-percent="trialOverviewDiscountPercent"
        @continue="handleOverviewStepDone"
        @request-upgrade="hide"
      />
      <template v-if="canManageBilling">
        <BillingCheckoutPlanSelectionStep
          v-if="currentStep === EBillingCheckoutStep.PLAN_SELECTION"
          :active="isOpen"
          :initial-plan="selectedPlan"
          :initial-interval="selectedBillingInterval"
          :initial-team-quantity="selectedTeamQuantity"
          :initial-student-quantity="selectedStudentQuantity"
          :initial-invoice-payment-requested="selectedInvoicePaymentRequested"
          :is-checkout-flow="needsCheckout"
          :checkout-source="checkoutSource"
          :disabled="isSubmitting"
          :show-create-offer-link="canCreateBillingOffer"
          @continue="handlePlanSelectionStepDone"
          @create-offer="handleCreateOfferFromPlanSelection"
          @plan-draft-change="handlePlanSelectionPlanDraftChange"
          @interval-draft-change="handlePlanSelectionIntervalDraftChange"
          @quantity-draft-change="handlePlanSelectionQuantityDraftChange"
        />
        <BillingCheckoutPaymentMethodStep
          v-if="currentStep === EBillingCheckoutStep.PAYMENT_METHOD"
          v-model="selectedPaymentMethod"
          :disabled="isSubmitting"
          @back="handleBackFromPaymentMethod"
          @continue="handlePaymentMethodStepDone"
        />
        <BillingCheckoutInvoiceAddressStep
          v-if="currentStep === EBillingCheckoutStep.INVOICE_ADDRESS"
          :address="invoiceAddress"
          :draft-address="invoiceAddressDraft"
          :disabled="isSubmitting"
          @back="handleBackFromInvoiceAddress"
          @continue="handleInvoiceAddressStepDone"
          @draft-change="invoiceAddressDraft = $event"
        />
        <BillingCheckoutInvoiceSuccessStep
          v-if="currentStep === EBillingCheckoutStep.INVOICE_SUCCESS"
          :product="selectedPlanWithMeta"
          :disabled="isSubmitting"
          @done="hide('completed')"
          @view-invoices="handleViewBillingDocuments"
        />
        <CheckoutSuccessMainContent
          v-if="currentStep === EBillingCheckoutStep.SUCCESS"
          :product="selectedPlanWithMeta"
          :disable-primary-action="isSubmitting"
          @done="hide('completed')"
        />
      </template>
      <IconButton
        v-if="
          currentStep !== EBillingCheckoutStep.OVERVIEW &&
          currentStep !== EBillingCheckoutStep.PLAN_SELECTION &&
          currentStep !== EBillingCheckoutStep.PAYMENT_METHOD &&
          currentStep !== EBillingCheckoutStep.INVOICE_SUCCESS
        "
        class="absolute z-10 max-md:right-md max-md:top-md"
        :class="currentStep === EBillingCheckoutStep.INVOICE_ADDRESS ? 'right-lg top-lg' : 'right-xl top-xl'"
        type="button"
        size="button"
        :icon-name="EIcon.X"
        theme="text"
        @click="hide()"
      />
    </div>
    <LoadingSpinner v-if="isSubmitting" />
  </DialogBase>
</template>
